// Skills v2 library — skill detail drawer (2026-08-19).
//
// Mirrors MarketplaceViewer's drawer → tabs → footer shape, lighter. Two tabs:
//   Overview — display_name, description, category, lift, data-fit, visibility,
//              install count, + an honest "add from an agent" line.
//   Reviews  — lazy skillsApi.skillRatings(name) list + a 1-5 star rate widget
//              (skillsApi.rateSkill) with an inline "Thanks — rated ★N" toast.
// No alert(): every outcome surfaces inline.

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { COLORS } from "../../theme/colors";
import { MATERIALS } from "../../theme/materials";
import { skillsApi } from "../../api/client";
import { formatLift, dataFitLabel, stars, installLabel, toneColor } from "./skillLibFormat";

const RED = COLORS.red || "#ef4444";
const GREEN = COLORS.green || "#10b981";
const BORDER = COLORS.border || "#e2e5eb";
const MUTED = COLORS.textMuted || "#6b7280";
const DIM = COLORS.textDim || "#9090a8";

function unwrapArray(res) {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.data)) return res.data;
  return [];
}

function StarPicker({ value, onChange }) {
  return (
    <div style={{ display: "inline-flex", gap: 2 }} role="radiogroup" aria-label="Your rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          aria-label={`${n} star${n === 1 ? "" : "s"}`}
          onClick={() => onChange(n)}
          style={{
            background: "transparent", border: "none", cursor: "pointer",
            fontSize: 20, lineHeight: 1, padding: "0 1px",
            color: n <= value ? (COLORS.orange || "#f59e0b") : DIM,
          }}
        >{n <= value ? "★" : "☆"}</button>
      ))}
    </div>
  );
}

function ReviewsTab({ skill }) {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRating, setUserRating] = useState(0);
  const [userReview, setUserReview] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [thanks, setThanks] = useState(null); // {rating} after a successful rate

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setRatings(unwrapArray(await skillsApi.skillRatings(skill.name)));
    } catch {
      setError("Couldn't load reviews just now.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [skill.name]);

  const submit = async () => {
    if (!userRating) {
      setError("Pick a star rating first.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await skillsApi.rateSkill(skill.name, { rating: userRating, review: userReview || undefined });
      setThanks({ rating: userRating });
      setUserReview("");
      await load();
    } catch (e) {
      setError(e?.message || "Couldn't save your rating.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div style={{
        border: `1px solid ${BORDER}`, borderRadius: 12, padding: 14, marginBottom: 14,
        background: COLORS.surface,
      }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: COLORS.text, marginBottom: 8 }}>
          Rate this skill
        </div>
        <StarPicker value={userRating} onChange={(n) => { setUserRating(n); setThanks(null); }} />
        <textarea
          aria-label="Review"
          placeholder="Optional — what worked, what didn't?"
          value={userReview}
          onChange={(e) => setUserReview(e.target.value)}
          rows={2}
          style={{
            width: "100%", boxSizing: "border-box", marginTop: 8, resize: "vertical",
            fontSize: 12.5, color: COLORS.text, background: COLORS.surfaceHover || COLORS.surface,
            border: `1px solid ${BORDER}`, borderRadius: 8, padding: "8px 10px",
          }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            style={{
              background: COLORS.accent, color: "#fff", border: "none", borderRadius: 8,
              padding: "7px 14px", fontSize: 12.5, fontWeight: 600,
              cursor: submitting ? "default" : "pointer", opacity: submitting ? 0.6 : 1,
            }}
          >{submitting ? "Saving…" : "Submit rating"}</button>
          {thanks && (
            <span style={{ fontSize: 12.5, color: GREEN }}>
              Thanks — rated {"★".repeat(thanks.rating)}
            </span>
          )}
        </div>
      </div>

      {error && <div style={{ fontSize: 12.5, color: RED, marginBottom: 10 }}>{error}</div>}
      {loading ? (
        <div style={{ fontSize: 12.5, color: DIM }}>Loading reviews…</div>
      ) : ratings.length === 0 ? (
        <div style={{ fontSize: 12.5, color: DIM }}>No reviews yet — be the first.</div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {ratings.map((r, i) => (
            <div key={i} style={{ border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 12px" }}>
              <div style={{ fontSize: 13, color: COLORS.orange || "#f59e0b" }}>{stars(r.rating)}</div>
              {r.review && <div style={{ fontSize: 12.5, color: MUTED, marginTop: 4 }}>{r.review}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OverviewBody({ skill, onAdd }) {
  const lift = formatLift(skill.eval_lift);
  const fit = dataFitLabel(skill.data_fit);
  const KV = ({ k, v }) => (
    <div style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: 8, fontSize: 12.5, padding: "4px 0" }}>
      <div style={{ color: DIM }}>{k}</div>
      <div style={{ color: COLORS.text }}>{v}</div>
    </div>
  );
  return (
    <div>
      <div style={{ fontSize: 13, color: MUTED, marginBottom: 12, lineHeight: 1.5 }}>
        {skill.description || "No description yet."}
      </div>
      {skill.category && <KV k="Category" v={skill.category} />}
      {lift && <KV k="Proven lift" v={<span style={{ color: GREEN, fontWeight: 600 }}>{lift} on your data</span>} />}
      {fit && <KV k="Data fit" v={fit} />}
      {skill.visibility && <KV k="Visibility" v={skill.visibility} />}
      {installLabel(skill.install_count) && <KV k="Installs" v={installLabel(skill.install_count)} />}
      {typeof skill.avg_rating === "number" && (
        <KV k="Rating" v={<span style={{ color: COLORS.orange || "#f59e0b" }}>{stars(skill.avg_rating)}</span>} />
      )}
      <button
        type="button"
        onClick={() => onAdd && onAdd(skill)}
        style={{
          marginTop: 16, width: "100%", font: "inherit", fontSize: 13.5, fontWeight: 700, color: "#fff",
          background: COLORS.accent, border: "none", borderRadius: 10, padding: "10px 14px",
          cursor: "pointer", boxShadow: `0 0 16px ${COLORS.accentGlow}`,
        }}
      >＋ Add to an agent</button>
      <div style={{ fontSize: 11.5, color: DIM, marginTop: 8, lineHeight: 1.5 }}>
        Pick the agent that needs it — we attach the skill and take you straight to that agent.
        You can preview its impact and remove it there any time.
      </div>
    </div>
  );
}

export default function SkillDetailDrawer({ skill, onClose, onAdd, onEdit }) {
  const [tab, setTab] = useState("overview");
  // Close on Escape. Hook MUST stay above the early return (React #310).
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!skill) return null;

  const TabBtn = ({ id, label }) => {
    const on = tab === id;
    return (
      <button
        type="button"
        onClick={() => setTab(id)}
        style={{
          background: "transparent", border: "none", cursor: "pointer",
          padding: "9px 4px", marginRight: 16, fontSize: 12.5,
          fontWeight: on ? 600 : 500, color: on ? COLORS.text : DIM,
          borderBottom: `2px solid ${on ? COLORS.accent : "transparent"}`,
        }}
      >{label}</button>
    );
  };

  // Portal to <body>: the app's glass surfaces use backdrop-filter, which
  // establishes a containing block for position:fixed descendants — rendered
  // in-tree the drawer re-anchored to that box ("opened on the left" and its
  // close ✕ landed off where users looked). A portal escapes it so
  // position:fixed anchors to the viewport as intended. The full-screen
  // backdrop + Escape give real dismiss affordances (the faint ✕ was the only
  // way to close, and users couldn't find it).
  return createPortal(
    <>
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.38)", zIndex: 2147483000 }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${skill.display_name || skill.name} details`}
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0, width: "min(440px, 100vw)",
          ...MATERIALS.thickGlass(), borderRadius: 0,
          borderLeft: `1px solid ${BORDER}`, boxShadow: MATERIALS.spatialShadow(),
          display: "flex", flexDirection: "column", zIndex: 2147483001,
        }}
      >
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: `1px solid ${BORDER}` }}>
        <span style={{ fontSize: 22 }}>{skill.icon || "✦"}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>
            {skill.display_name || skill.name}
          </div>
        </div>
        {onEdit && skill.visibility === "tenant" && (
          <button
            type="button"
            onClick={() => onEdit(skill)}
            style={{
              background: COLORS.accent, color: "#fff", border: "none", cursor: "pointer",
              fontSize: 12.5, fontWeight: 700, borderRadius: 8, padding: "7px 12px", flex: "0 0 auto",
            }}
          >✎ Edit</button>
        )}
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          style={{
            background: COLORS.surface, border: `1px solid ${BORDER}`, cursor: "pointer",
            fontSize: 16, lineHeight: 1, color: COLORS.text, borderRadius: 8,
            width: 30, height: 30, display: "grid", placeItems: "center", flex: "0 0 auto",
          }}
        >✕</button>
      </div>

      <div style={{ display: "flex", padding: "0 16px", borderBottom: `1px solid ${BORDER}` }}>
        <TabBtn id="overview" label="Overview" />
        <TabBtn id="reviews" label="Reviews" />
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        {tab === "overview" ? <OverviewBody skill={skill} onAdd={onAdd} /> : <ReviewsTab skill={skill} />}
      </div>
      </div>
    </>,
    document.body,
  );
}
